import { ArrowLeft } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const CarbFiberRatioDetail = () => {
  const navigate = useNavigate();

  const dailyData = Array.from({ length: 7 }, (_, i) => ({
    meal: `Meal ${i + 1}`,
    ratio: Math.floor(Math.random() * 8 + 5),
  }));

  const weeklyData = [
    { day: "Mon", ratio: 8 },
    { day: "Tue", ratio: 10 },
    { day: "Wed", ratio: 6 },
    { day: "Thu", ratio: 12 },
    { day: "Fri", ratio: 9 },
    { day: "Sat", ratio: 11 },
    { day: "Sun", ratio: 7 },
  ];

  const monthlyData = [
    { week: "Week 1", ratio: 9 },
    { week: "Week 2", ratio: 10 },
    { week: "Week 3", ratio: 8 },
    { week: "Week 4", ratio: 9 },
  ];

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/home")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">
            Carb-to-Fiber Ratio
          </h1>
          <div className="w-10" />
        </div>

        {/* Today's Stats */}
        <Card className="from-warning/10 to-warning/5 rounded-2xl border-0 bg-gradient-to-br p-6 shadow-sm">
          <div className="text-center">
            <p className="mb-2 text-sm text-muted-foreground">
              Today's Carb-to-Fiber Ratio
            </p>
            <div className="mb-2 text-5xl font-bold text-foreground">9</div>
            <p className="text-sm text-muted-foreground">Good (Target: ≤ 12)</p>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="bg-warning h-full rounded-full"
                style={{ width: "60%" }}
              />
            </div>
          </div>
        </Card>

        {/* What is Carb-to-Fiber Ratio */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            What is Carb-to-Fiber Ratio?
          </h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            This ratio compares total carbohydrates to dietary fiber in your
            diet. A lower ratio indicates higher fiber content relative to
            carbs, which is beneficial for gut health, blood sugar control, and
            sustained energy. Whole foods naturally have better ratios, while
            processed foods tend to have high ratios.
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Excellent</span>
              <span className="text-success font-semibold">≤ 7</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Good</span>
              <span className="font-semibold">≤ 12</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Moderate</span>
              <span className="text-warning font-semibold">≤ 20</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Poor</span>
              <span className="font-semibold text-destructive">Above 20</span>
            </div>
          </div>
        </Card>

        {/* Tabs for Daily/Weekly/Monthly */}
        <Card className="rounded-2xl border-0 shadow-sm">
          <Tabs defaultValue="daily" className="w-full">
            <TabsList className="m-4 grid w-full grid-cols-3 p-1">
              <TabsTrigger value="daily">Daily</TabsTrigger>
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>

            <TabsContent value="daily" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={dailyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="meal"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={10}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar
                    dataKey="ratio"
                    fill="hsl(var(--warning))"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </TabsContent>

            <TabsContent value="weekly" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={weeklyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="day"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="ratio"
                    stroke="hsl(var(--warning))"
                    strokeWidth={2}
                    dot={{ fill: "hsl(var(--warning))" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </TabsContent>

            <TabsContent value="monthly" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthlyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="week"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar
                    dataKey="ratio"
                    fill="hsl(var(--warning))"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Improvement Tips */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            Ways to Improve Your Ratio
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>
                Choose whole grains over refined grains (brown rice, quinoa,
                oats)
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>Add beans and lentils to your meals</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>Eat fruits with the skin on when possible</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-warning mt-0.5">•</span>
              <span>Snack on nuts, seeds, and high-fiber vegetables</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default CarbFiberRatioDetail;
